import { useMemo, useState } from 'react';
import { Box, Button, Flex, Popover } from '@mantine/core';
import { useMediaQuery } from '@mantine/hooks';
import { IconMenu2 } from '@tabler/icons-react';
import { QuickStatusFilterButton } from './QuickStatusFilterButton';

const statusButtonLayouts = {
  compact: {
    w: 'auto',
    h: 'auto',
    contentDirection: 'column',
    contentGap: 3,
  },
  inline: {
    w: '',
    contentDirection: 'column',
    contentGap: 1,
    styles: {
      root: { width: '100%' },
      label: { width: '100%' },
    },
  },
  dropdown: {
    w: '100%',
    contentDirection: 'row',
    contentGap: 'lg',
    styles: {
      root: { display: 'flex', justifyContent: 'flex-end', width: '100%' },
      label: { width: '100%' },
    },
  },
} as const;

export interface StatusFilterButtonItem<TKey extends string = string> {
  key: TKey;
  label: string;
  count: number;
  active: boolean;
  dataTestId?: string;
}

interface StatusFilterButtonsProps<TKey extends string = string> {
  items: StatusFilterButtonItem<TKey>[];
  onSelect: (key: TKey) => void;
  topKeys?: TKey[];
  isLoading?: boolean;
  moreLabel?: string;
  filtersLabel?: string;
  collapseBreakpoint?: string;
  enableCollapseOnNarrow?: boolean;
  sortOverflowByCount?: boolean;
  muted?: boolean;
  compact?: boolean;
}

export const QuickStatusFilterButtons = <TKey extends string = string>({
  items,
  onSelect,
  topKeys,
  isLoading = false,
  moreLabel = 'More',
  filtersLabel = 'Filters',
  collapseBreakpoint = '(min-width: 1250px)',
  enableCollapseOnNarrow = true,
  sortOverflowByCount = true,
  muted = false,
  compact = false,
}: StatusFilterButtonsProps<TKey>) => {
  const isWideScreen = useMediaQuery(collapseBreakpoint);
  const [showFiltersOnNarrow, setShowFiltersOnNarrow] = useState(false);

  const topSet = useMemo(() => new Set(topKeys ?? []), [topKeys]);

  const top = useMemo(() => {
    if (!topKeys?.length) return items;

    const itemMap = new Map(items.map((item) => [item.key, item]));
    return topKeys
      .map((key) => itemMap.get(key))
      .filter((item): item is StatusFilterButtonItem<TKey> => Boolean(item));
  }, [items, topKeys]);

  const bottom = useMemo(() => {
    if (!topKeys?.length) return [];
    return items.filter((item) => !topSet.has(item.key));
  }, [items, topKeys, topSet]);

  const sortedBottom = useMemo(() => {
    if (!sortOverflowByCount) return bottom;
    return [...bottom].sort((a, b) => b.count - a.count);
  }, [bottom, sortOverflowByCount]);

  const bottomSum = useMemo(
    () => bottom.reduce((sum, item) => sum + item.count, 0),
    [bottom]
  );
  const bottomActive = bottom.some((item) => item.active);

  const shouldShowFilters =
    !enableCollapseOnNarrow || isWideScreen || showFiltersOnNarrow;

  if (isLoading) return null;

  const renderStatusButton = (
    item: StatusFilterButtonItem<TKey>,
    layout: keyof typeof statusButtonLayouts
  ) => (
    <QuickStatusFilterButton
      key={item.key}
      onClick={() => onSelect(item.key)}
      active={item.active}
      muted={muted}
      compact={compact}
      label={item.label}
      count={item.count}
      dataTestId={item.dataTestId}
      h="100%"
      {...statusButtonLayouts[layout]}
    />
  );

  return (
    <Flex direction="column" gap="xs" w="100%">
      {enableCollapseOnNarrow && !isWideScreen && (
        <Button
          onClick={() => setShowFiltersOnNarrow((prev) => !prev)}
          leftSection={<IconMenu2 />}
          w="fit-content"
        >
          {filtersLabel}
        </Button>
      )}

      {shouldShowFilters && (
        <Flex
          wrap={compact ? 'nowrap' : 'wrap'}
          align="center"
          w="100%"
          gap={compact ? 2 : 'sm'}
        >
          {top.map((item) =>
            renderStatusButton(item, compact ? 'compact' : 'inline')
          )}
          {bottom.length > 0 && (
            <Popover
              position="bottom-start"
              shadow="md"
              withArrow
              zIndex={1000}
            >
              <Popover.Target>
                <QuickStatusFilterButton
                  active={bottomActive}
                  muted={muted}
                  label={moreLabel}
                  count={bottomSum}
                  onClick={() => undefined}
                  h="100%"
                />
              </Popover.Target>
              <Popover.Dropdown
                style={{
                  padding: 0,
                  overflowY: 'auto',
                  maxHeight: '15.5rem',
                  minWidth: '4.5rem',
                }}
              >
                <Box w="100%">
                  <Flex direction="column" gap="md" w="100%" pt="xs">
                    {sortedBottom.map((item) =>
                      renderStatusButton(item, 'dropdown')
                    )}
                  </Flex>
                </Box>
              </Popover.Dropdown>
            </Popover>
          )}
        </Flex>
      )}
    </Flex>
  );
};
